import { useEffect, useState } from 'react'
import { apiUrl } from './api'
import { setPenzionExternalFromContent } from './booking'

/**
 * Ceny zobrazované na webu — editují se v brigádnické appce (Admin → Web · ceny),
 * backend /api/sobacov/web/prices. Web má vždy hardcoded fallback, takže výpadek
 * API nikdy nerozbije zobrazení.
 */

export type WebPrices = Partial<
  Record<
    | 'penzion_od' | 'chatka_od' | 'tiny_od' | 'bivak_od' | 'karavan_od'
    | 'tenis_h' | 'beach_h' | 'sauna_block',
    number
  >
>

let cache: WebPrices | null = null
// Jeden rozpracovaný dotaz pro všechny komponenty — dřív každá karta volala
// /web/prices a /web/content sama (12× při načtení stránky).
let pricesInflight: Promise<WebPrices | null> | null = null

function fetchPrices(): Promise<WebPrices | null> {
  if (!pricesInflight) {
    pricesInflight = fetch(apiUrl('/api/sobacov/web/prices'))
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => (j?.prices ? (cache = j.prices) : null))
      .catch(() => null)
  }
  return pricesInflight
}

export function useWebPrices(): WebPrices {
  const [p, setP] = useState<WebPrices>(cache || {})
  useEffect(() => {
    if (cache) return
    let dead = false
    fetchPrices().then((v) => { if (v && !dead) setP(v) })
    return () => { dead = true }
  }, [])
  return p
}

export const czk = (n: number) => n.toLocaleString('cs-CZ')

/**
 * Obsahové overridy hlavní stránky (fotky) — editují se v appce (Admin → Web),
 * backend /api/sobacov/web/content. Bez override platí bundlované fotky.
 */
export type WebContent = {
  hero_image?: string
  gallery_images?: string[]
  [key: string]: string | string[] | undefined
}

let contentCache: WebContent | null = null
let contentInflight: Promise<WebContent | null> | null = null

function fetchContent(): Promise<WebContent | null> {
  if (!contentInflight) {
    contentInflight = fetch(apiUrl('/api/sobacov/web/content'))
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!j?.content) return null
        contentCache = j.content
        setPenzionExternalFromContent(contentCache as Record<string, unknown>)   // penzion × Booking.com
        return contentCache
      })
      .catch(() => null)
  }
  return contentInflight
}

export function useWebContent(): WebContent {
  const [c, setC] = useState<WebContent>(contentCache || {})
  useEffect(() => {
    if (contentCache) return
    let dead = false
    fetchContent().then((v) => { if (v && !dead) setC(v) })
    return () => { dead = true }
  }, [])
  return c
}
